import { useCallback } from 'react';
import { MAP_SYMBOLS } from '@/lib/map-assets';
import type { PlacedSymbol } from './types';

interface SymbolInteractionInput {
    mapWidth: number;
    mapHeight: number;
    brushSize: number;
    symbolSize: number;
    stampDensity: number;
    activeSymbol: string;
    activeSymbols: string[];
    placedSymbols: PlacedSymbol[];
    landMaskRef: React.RefObject<Uint8Array | null>;
    lastPlacementPos: React.MutableRefObject<{ x: number; y: number } | null>;
    setPlacedSymbols: React.Dispatch<React.SetStateAction<PlacedSymbol[]>>;
}

const MAX_SYMBOLS = 2000;

function generateUid(): string {
    return Math.random().toString(36).substring(2, 9);
}

/**
 * Returns memoised callbacks for symbol placement operations extracted from map-canvas.tsx:
 * - eraseSymbolsAt — removes all symbols within a radius
 * - spraySymbolsAt — scatters symbols in brush mode (distance-gated)
 * - sprayStampsAt  — auto-stamps terrain symbols along a stroke (land stamp tool)
 */
export function useSymbolInteraction({
    mapWidth,
    mapHeight,
    brushSize,
    symbolSize,
    stampDensity,
    activeSymbol,
    activeSymbols,
    placedSymbols,
    landMaskRef,
    lastPlacementPos,
    setPlacedSymbols,
}: SymbolInteractionInput) {
    const eraseSymbolsAt = useCallback(
        (bx: number, by: number, radius: number) => {
            setPlacedSymbols(prev =>
                prev.filter(sym => Math.hypot(sym.x - bx, sym.y - by) >= radius),
            );
        },
        [setPlacedSymbols],
    );

    const spraySymbolsAt = useCallback(
        (bx: number, by: number) => {
            if (placedSymbols.length >= MAX_SYMBOLS) return;

            const spacing = Math.max(10, brushSize * 5.0 * (1.2 - stampDensity));

            if (lastPlacementPos.current) {
                const dist = Math.hypot(bx - lastPlacementPos.current.x, by - lastPlacementPos.current.y);
                if (dist < spacing) return;
            }

            lastPlacementPos.current = { x: bx, y: by };

            const count = Math.max(1, Math.floor(stampDensity * 8));
            const newSyms: PlacedSymbol[] = [];
            const symbolsToUse = activeSymbols.length > 0 ? activeSymbols : [activeSymbol];

            for (let i = 0; i < count; i++) {
                if (placedSymbols.length + newSyms.length >= MAX_SYMBOLS) break;
                const offset = brushSize * 10.0;
                const angle = Math.random() * Math.PI * 2;
                const dist = Math.random() * offset;
                const rx = bx + Math.cos(angle) * dist;
                const ry = by + Math.sin(angle) * dist;
                const rSize = symbolSize * (0.8 + Math.random() * 0.4);
                const sId = symbolsToUse[Math.floor(Math.random() * symbolsToUse.length)];

                newSyms.push({
                    uid: generateUid(),
                    id: sId,
                    x: Math.max(0, Math.min(mapWidth, rx)),
                    y: Math.max(0, Math.min(mapHeight, ry)),
                    size: Math.max(5, rSize),
                    rotation: Math.floor(Math.random() * 30) - 15,
                });
            }

            if (newSyms.length > 0) {
                setPlacedSymbols(prev => [...prev, ...newSyms]);
            }
        },
        [placedSymbols, brushSize, stampDensity, activeSymbols, activeSymbol, symbolSize, mapWidth, mapHeight, lastPlacementPos, setPlacedSymbols],
    );

    const sprayStampsAt = useCallback(
        (x: number, y: number, lx: number, ly: number, brush: string) => {
            const landMask = landMaskRef.current;
            if (!landMask) return;

            const terrainMap: Record<string, { category: string; subcategory?: string }> = {
                mountain: { category: 'mountains' },
                forest: { category: 'trees' },
                dune: { category: 'terrain', subcategory: 'Desert' },
                swamp: { category: 'terrain', subcategory: 'Swamp' },
            };
            const terrain = terrainMap[brush];
            if (!terrain) return;

            const radius = brushSize * 10;
            const variations = MAP_SYMBOLS.filter(
                s => s.category === terrain.category && (!terrain.subcategory || s.subcategory === terrain.subcategory) && s.src,
            );
            if (variations.length === 0) return;

            const dist = Math.hypot(x - lx, y - ly);
            const steps = Math.max(1, Math.floor(dist / (brushSize * 4)));
            const newStamps: PlacedSymbol[] = [];

            for (let i = 0; i <= steps; i++) {
                const interpX = lx + (x - lx) * (i / steps);
                const interpY = ly + (y - ly) * (i / steps);
                if (Math.random() > stampDensity * 1.5) continue;

                const angle = Math.random() * Math.PI * 2;
                const offsetDist = Math.random() * radius * 0.7;
                const px = Math.floor(interpX + Math.cos(angle) * offsetDist);
                const py = Math.floor(interpY + Math.sin(angle) * offsetDist);

                if (px < 0 || px >= mapWidth || py < 0 || py >= mapHeight) continue;
                const idx = py * mapWidth + px;
                if (landMask[idx] <= 128) continue;

                const symbol = variations[Math.floor(Math.random() * variations.length)];
                const stampSize = brushSize * 4 * (0.8 + Math.random() * 0.4);

                newStamps.push({
                    uid: generateUid(),
                    id: symbol.id,
                    x: px,
                    y: py,
                    size: stampSize,
                    rotation: Math.floor(Math.random() * 20) - 10,
                });
            }

            if (newStamps.length > 0) {
                setPlacedSymbols(prev => {
                    if (prev.length + newStamps.length >= MAX_SYMBOLS) return prev;
                    return [...prev, ...newStamps];
                });
            }
        },
        [brushSize, stampDensity, mapWidth, mapHeight, landMaskRef, setPlacedSymbols],
    );

    return { eraseSymbolsAt, spraySymbolsAt, sprayStampsAt };
}
